<template>
  <b-modal
    id="modal-topup"
    centered
    title="Add Top-Up"
    hide-footer
    @hidden="resetModal"
    no-close-on-backdrop
    size="md"
  >
    <b-form novalidate @submit.prevent="submitTopUp">
      <div v-if="Object.keys(serverErrors).length" class="alert alert-danger" role="alert">
        <div v-for="(messages, field) in serverErrors" :key="field">
          {{ Array.isArray(messages) ? messages.join(' ') : messages }}
        </div>
      </div>
      <b-form-group label="Amount" label-for="amount" class="required-label">
        <b-form-input
          id="amount"
          type="number"
          step="any"
          v-model="topupAmount"
          :state="fieldError('amount') ? false : null"
          placeholder="0.00"
          @input="calculatePerPostCost"
        />
        <small class="text-danger">{{ fieldError('amount') }}</small>
      </b-form-group>

      <b-form-group label="Currency" label-for="currency" class="required-label">
        <v-select
          id="currency"
          v-model="selectedCurrency"
          :options="filteredCurrencyOptions"
          :reduce="(item) => item.id"
          label="currency"
          placeholder="Select Currency"
          @input="calculatePerPostCost"
        />
        <small class="text-danger">{{ fieldError('currency_id') }}</small>
      </b-form-group>

      <b-form-group label="Posts Added" label-for="posts_added" class="required-label">
        <b-form-input
          id="posts_added"
          type="number"
          v-model="topupPosts"
          :state="fieldError('posts_added') ? false : null"
          placeholder="0"
          @input="calculatePerPostCost"
        />
        <small class="text-danger">{{ fieldError('posts_added') }}</small>
      </b-form-group>

      <b-form-group label="Top Up Date" label-for="topup_date" class="required-label">
        <b-form-datepicker
          id="topup_date"
          v-model="topupDate"
          :state="fieldError('topup_date') ? false : null"
          locale="en-US"
          placeholder="Select Date"
          today-button
          close-button
          reset-button
        />
        <small class="text-danger">{{ fieldError('topup_date') }}</small>
      </b-form-group>

      <b-form-group v-if="calculatedCost > 0" label="Per Post Cost (Preview)">
        <b-form-input :value="currencySymbol + calculatedCost" disabled />
      </b-form-group>

      <b-form-group label="Attachment" label-for="attachment">
        <b-form-file
          id="attachment"
          v-model="attachmentFiles"
          multiple
          placeholder="Choose files or drop here..."
          drop-placeholder="Drop files here..."
        />
      </b-form-group>

      <b-form-group label="Note" label-for="note">
        <b-form-textarea
          id="note"
          v-model="topupNote"
          placeholder="Optional note"
          rows="2"
        />
      </b-form-group>

      <template v-if="isSubmitLoading">
        <b-button class="float-right" variant="primary" disabled>
          <b-spinner small />
          Loading...
        </b-button>
      </template>
      <template v-else>
        <b-button
          type="submit"
          class="float-right"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
        >
          Submit
        </b-button>
      </template>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormTextarea,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "TopUpModal",
  components: {
    BModal,
    BForm,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormTextarea,
    BButton,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    authorId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      topupAmount: "",
      topupPosts: "",
      topupDate: (() => {
        const d = new Date();
        const m = `${d.getMonth() + 1}`.padStart(2, "0");
        const day = `${d.getDate()}`.padStart(2, "0");
        return `${d.getFullYear()}-${m}-${day}`;
      })(),
      topupNote: "",
      attachmentFiles: [],
      selectedCurrency: null,
      filteredCurrencyOptions: [],
      isSubmitLoading: false,
      serverErrors: {},
      calculatedCost: 0,
    };
  },
  computed: {
    currencySymbol() {
      const found = this.filteredCurrencyOptions.find((c) => c.id === this.selectedCurrency);
      return found ? found.currency + " " : "";
    },
    todayDateString() {
      const d = new Date();
      const m = `${d.getMonth() + 1}`.padStart(2, "0");
      const day = `${d.getDate()}`.padStart(2, "0");
      return `${d.getFullYear()}-${m}-${day}`;
    },
  },
  async mounted() {
    await this.loadCurrencies();
  },
  methods: {
    async loadCurrencies() {
      try {
        const response = await this.$api.get("api/currencies/all");
        this.filteredCurrencyOptions = response?.data?.data || [];
      } catch (error) {
        // silent
      }
    },

    calculatePerPostCost() {
      const amount = parseFloat(this.topupAmount);
      const posts = parseInt(this.topupPosts, 10);
      if (amount > 0 && posts > 0) {
        this.calculatedCost = (amount / posts).toFixed(2);
      } else {
        this.calculatedCost = 0;
      }
    },

    fieldError(field) {
      const errors = this.serverErrors[field];
      return Array.isArray(errors) ? errors[0] : errors || "";
    },

    resetModal() {
      this.serverErrors = {};
      this.topupDate = this.todayDateString;
      this.topupAmount = "";
      this.topupPosts = "";
      this.topupNote = "";
      this.attachmentFiles = [];
      this.selectedCurrency = null;
      this.calculatedCost = 0;
    },

    submitTopUp: async function () {
      if (this.isSubmitLoading) return;
      this.isSubmitLoading = true;
      this.serverErrors = {};
      try {
        const formData = new FormData();
        formData.append("amount", this.topupAmount);
        formData.append("posts_added", this.topupPosts);
        formData.append("topup_date", this.topupDate);
        formData.append("currency_id", this.selectedCurrency ?? "");
        if (this.topupNote) formData.append("note", this.topupNote);
        if (this.attachmentFiles && this.attachmentFiles.length) {
          for (let i = 0; i < this.attachmentFiles.length; i++) {
            formData.append("attachments[]", this.attachmentFiles[i]);
          }
        }
        await this.$api.post(
          `/api/outreach-authors/${this.authorId}/topup`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        this.$bvModal.hide("modal-topup");
        this.$emit("topup-created");

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "BellIcon",
            variant: "success",
            text: "Top-Up Submitted Successfully",
          },
        });
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message || "Unable to submit the top-up. Please try again.",
          },
        });
        if (error?.response?.data?.errors) {
          this.serverErrors = error.response.data.errors;
        }
      } finally {
        this.isSubmitLoading = false;
      }
    },
  },
};
</script>
